import React, { ReactNode, useState } from 'react'
import { T } from '@transifex/react'
import { Checkbox, CheckboxProps } from 'src/antd'

import { InfoCircleFilled } from 'src/icons/antd-compat'
import { orange500 } from 'src/styles/color-theme'

import { WhIconAntdExclamationTriangleFill } from '../../wh-icons-antd'
import { ButtonCancel, ButtonSubmit } from '../Buttons'
import { Typography } from '../Typography'

type TVariant = 'default' | 'danger' | 'warning'

export type TConfirmProps = {
  variant?: TVariant
  title: ReactNode
  children?:
    | ((args: {
        jsxCancelButton: JSX.Element
        jsxSubmitButton: JSX.Element
      }) => ReactNode)
    | ReactNode
  cancelButtonProps?: React.ComponentProps<typeof ButtonSubmit>
  submitButtonProps?: Omit<
    React.ComponentProps<typeof ButtonSubmit>,
    'onClick'
  > & {
    onClick?: (checked: boolean) => void
  }
  checkboxProps?: CheckboxProps & { disableSubmitWhenUnchecked?: boolean }
}

const colorMap: Record<TVariant, string> = {
  default: `#1ea7fd`,
  danger: `#FF4D4F`,
  warning: orange500,
}

const iconMap: Record<TVariant, JSX.Element> = {
  default: <InfoCircleFilled />,
  danger: <WhIconAntdExclamationTriangleFill />,
  warning: <WhIconAntdExclamationTriangleFill />,
}

export const Confirm = ({
  variant = 'default',
  title,
  children,
  cancelButtonProps,
  submitButtonProps,
  checkboxProps,
}: TConfirmProps) => {
  const [checked, setChecked] = useState(false)

  const jsxCancelButton = (
    <ButtonCancel {...cancelButtonProps}>
      {cancelButtonProps?.children ?? <T _str="Cancel" />}
    </ButtonCancel>
  )
  const jsxSubmitButton = (
    <ButtonSubmit
      {...submitButtonProps}
      disabled={
        submitButtonProps?.disabled ||
        (checkboxProps?.disableSubmitWhenUnchecked && !checked)
      }
      danger={variant === 'danger'}
      onClick={() => submitButtonProps?.onClick?.(checked)}
    >
      {submitButtonProps?.children ?? <T _str="Confirm" />}
    </ButtonSubmit>
  )

  return (
    <div style={{ display: 'flex' }}>
      <div style={{ width: 'calc(32px + 16px)' }}>
        {React.cloneElement(iconMap[variant], {
          style: { color: colorMap[variant], fontSize: '32px' },
        })}
      </div>
      <div style={{ width: 'calc(100% - 32px - 16px)' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: 'column',
          }}
        >
          <div style={{ minHeight: 40 }}>
            <Typography variant="body-14" ellipsis={false}>
              {title}
            </Typography>
          </div>
          {checkboxProps && (
            <Checkbox
              {...checkboxProps}
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
          )}
          {typeof children === 'function' ? (
            children({
              jsxCancelButton,
              jsxSubmitButton,
            })
          ) : (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'column',
              }}
            >
              {children}
              {/* PRO-929: the row had no wrap, so in a modal narrow enough
                  that two buttons no longer fit side by side the trailing
                  one was clipped — "Discard changes" on the unsaved-changes
                  dialog, which is the only way out of that dialog. antd
                  already clamps every modal to the viewport
                  (`max-width: calc(100vw - margin*2)`, and a tighter rule
                  below 576px), so the modal WIDTH was never the problem;
                  this row was. Wrapping changes nothing wherever the two
                  buttons fit, which is every desktop width. */}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'flex-end',
                  columnGap: 8,
                  rowGap: 8,
                  marginTop: 24,
                }}
              >
                {jsxCancelButton}
                {jsxSubmitButton}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
